import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';

import AspectRatio, { AspectRatioWrapper } from 'components/common/AspectRatio';
import FormInput from 'components/common/Form/Input';
import CloseIcon from 'components/common/svg/close';

import AttachFile from 'components/common/AttachFile';

import { ProfileService } from 'api/profile';
import useCurrentUser from 'store/currentUser';
import { userStorage } from 'store/storage';

import { verificationResolver } from 'config/resolvers';
import useTranslates from 'utils/translate';
import useToaster from 'hooks/useToaster';
import useGlobalSettings from 'store/globalSettings';
import { IGlobalSettingsMeta, SettingsService } from 'api/settings';
import SumsubWebSdk from '@sumsub/websdk-react';

import sx from './styles';
import { VerificationStatus } from 'types/auth';
import useScreen from 'hooks/useScreen';
import { useNavigate } from "react-router-dom";

interface VerificationDialogProps {
  open: boolean;
  onClose: () => void;
}

const VerificationDialog: FC<VerificationDialogProps> = ({ open, onClose }) => {
  const notify = useToaster();
  const { translate } = useTranslates();
  const [, currentUserActions] = useCurrentUser();
  const { isMobile } = useScreen();
  const navigate = useNavigate();
  const [
    {
      data: { meta = {} as IGlobalSettingsMeta },
    },
  ] = useGlobalSettings();
  const [accessToken, setAccessToken] = useState<string>('');
  const [accessTokenLoading, setAccessTokenLoading] = useState<boolean>(true);

  const { control, reset, watch, setValue, handleSubmit } = useForm({
    resolver: verificationResolver,
  });

  const fileValue = watch('url');

  useEffect(()=>{
    if(isMobile){
      navigate('/verification');
    }
  },[isMobile]);

  useEffect(() => {
    reset({});
  }, [open]);

  const handleExpiration = async (): Promise<string> => {
    try {
      setAccessTokenLoading(true);
      const result = await SettingsService.getSumsubToken();
      if (result?.token) {
        setAccessToken(result.token);
        setAccessTokenLoading(false);
        return result.token;
      }
    } catch (error) {
      console.error('Error fetching Sumsub token:', error);
    } finally {
      setAccessTokenLoading(false);
    }

    return '';
  };

  useEffect(() => {
    if (meta?.verificationSumsub) {
      handleExpiration();
    }
  }, [meta?.verificationSumsub]);

  const onUpdatePassword = (data) => {
    ProfileService.setDocuments({
      ...data,
    }).then((res) => {
      if (res?.id) {
        onClose();
        notify({
          message: translate('document_successfully_uploaded'),
        });
        currentUserActions.set({
          ...userStorage.get(),
          verificationStatus: VerificationStatus.AWAITING_VERIFICATION,
        });
      }
    });
  };

  const handleChangeFile = (fileUrl) => {
    setValue('url', fileUrl);
  };

  return (
    <Dialog keepMounted open={open} onClose={() => onClose()}>
      <DialogTitle>
        <Typography fontSize={18} fontWeight={600}>
          {translate('verification')}
        </Typography>
        <IconButton onClick={() => onClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack gap={3} mb={4} key={open ? 1 : 2}>
          <Typography textAlign="center">{translate('verification_description')}</Typography>
          {meta?.verificationSumsub ? (
            <>
              {!accessTokenLoading && <SumsubWebSdk accessToken={accessToken} expirationHandler={handleExpiration} />}
            </>
          ) : (
            <>
              <Box textAlign="center">
                {fileValue ? (
                  <AspectRatioWrapper>
                    <AspectRatio divide="8:5">
                      <Box sx={sx.image({ src: fileValue })}>
                        <IconButton sx={sx.removeFileIconButton} onClick={() => handleChangeFile('')}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </AspectRatio>
                  </AspectRatioWrapper>
                ) : (
                  <AttachFile onChange={handleChangeFile}>
                    <Button variant="contained">{translate('upload_passport')}</Button>
                  </AttachFile>
                )}
              </Box>
              <FormInput control={control} name="serialId" placeholder={translate('passport_series')} />
            </>
          )}
        </Stack>
        {!meta?.verificationSumsub && (
          <Button fullWidth variant="contained" onClick={handleSubmit(onUpdatePassword)} disabled={!fileValue}>
            {translate('verify_now_button')}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerificationDialog;
